import { useState, useEffect, useCallback, useRef } from 'react';

const TRAIL_THROTTLE_MS = 30;
const TRAIL_LIFE_MS = 500;
const SPARKLE_LIFE_MS = 600;
const SPARKLE_BURST_COUNT = 8;
const CLICK_FLASH_MS = 300;

/**
 * Tracks mouse position, hover state over clickable elements, click bursts,
 * and a throttled trail of recent positions.
 */
export const useCursorTracking = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isPointer, setIsPointer] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [trails, setTrails] = useState([]);
  const [sparkles, setSparkles] = useState([]);
  const trailIdRef = useRef(0);
  const sparkleIdRef = useRef(0);
  const lastTrailTime = useRef(0);

  const handleMouseMove = useCallback((e) => {
    setPosition({ x: e.clientX, y: e.clientY });

    const target = e.target;
    const clickable = target.closest('a, button, [role="button"], input, textarea, select, [data-clickable]');
    setIsPointer(!!clickable);

    const now = Date.now();
    if (now - lastTrailTime.current > TRAIL_THROTTLE_MS) {
      lastTrailTime.current = now;
      const id = trailIdRef.current++;
      setTrails((prev) => [...prev.slice(-12), { id, x: e.clientX, y: e.clientY, born: now }]);
    }
  }, []);

  const handleClick = useCallback((e) => {
    setIsClicking(true);
    setTimeout(() => setIsClicking(false), CLICK_FLASH_MS);

    const newSparkles = Array.from({ length: SPARKLE_BURST_COUNT }).map((_, i) => ({
      id: sparkleIdRef.current++,
      x: e.clientX,
      y: e.clientY,
      angle: (i / SPARKLE_BURST_COUNT) * Math.PI * 2,
      born: Date.now(),
    }));
    setSparkles((prev) => [...prev, ...newSparkles]);
  }, []);

  // GC old trails/sparkles
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTrails((prev) => prev.filter((t) => now - t.born < TRAIL_LIFE_MS));
      setSparkles((prev) => prev.filter((s) => now - s.born < SPARKLE_LIFE_MS));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleClick);
    };
  }, [handleMouseMove, handleClick]);

  return { position, isPointer, isClicking, trails, sparkles };
};

export { TRAIL_LIFE_MS, SPARKLE_LIFE_MS };
